/**
 * RecentlySaved Component
 *
 * Displays a horizontal scrollable row of recently saved properties.
 * Shows property thumbnail, title, and relative timestamp.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';

export interface RecentlySavedProperty {
  id: string;
  propertyId: string;
  title: string;
  imageUrl?: string | null;
  createdAt: string;
}

export interface RecentlySavedProps {
  properties: RecentlySavedProperty[];
  onPropertyClick?: (propertyId: string) => void;
  maxItems?: number;
}

export default function RecentlySaved({
  properties,
  onPropertyClick,
  maxItems = 5,
}: RecentlySavedProps) {
  const { t, i18n } = useTranslation('favorites');
  const isRTL = i18n.language === 'ar';
  const dateLocale = isRTL ? ar : enUS;

  // Sort by createdAt and limit to maxItems
  const sortedProperties = useMemo(() => {
    return [...properties]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, maxItems);
  }, [properties, maxItems]);

  // Don't render if no properties
  if (sortedProperties.length === 0) {
    return null;
  }

  const handleClick = (propertyId: string) => {
    if (onPropertyClick) {
      onPropertyClick(propertyId);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Section Header */}
      <div className="flex items-center gap-2">
        <i className="ri-time-line text-lg text-[var(--pv-color-primary)]" />
        <h3 className="text-sm font-semibold text-[var(--pv-color-text)]">
          {t('favorites.recentlySaved.title')}
        </h3>
      </div>

      {/* Horizontal Scrollable Row */}
      <div
        className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {sortedProperties.map((property) => {
          const timeAgo = formatDistanceToNow(new Date(property.createdAt), {
            addSuffix: true,
            locale: dateLocale,
          });

          const CardContent = (
            <div className="w-40 flex-shrink-0 snap-start">
              {/* Property Image */}
              <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-gray-100 dark:bg-[var(--pv-color-surface-soft)]">
                {property.imageUrl ? (
                  <img
                    src={property.imageUrl}
                    alt={property.title}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-home-4-line text-3xl text-gray-300 dark:text-gray-600" />
                  </div>
                )}
              </div>

              {/* Property Info */}
              <div className="mt-2">
                <p className="text-sm font-medium text-[var(--pv-color-text)] line-clamp-1">
                  {property.title}
                </p>
                <p className="text-xs text-[var(--pv-color-text-muted)] mt-0.5">
                  {t('favorites.recentlySaved.addedAgo', { time: timeAgo })}
                </p>
              </div>
            </div>
          );

          // If onPropertyClick provided, use button, otherwise use Link
          if (onPropertyClick) {
            return (
              <button
                key={property.id}
                onClick={() => handleClick(property.propertyId)}
                className="group text-start focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)] rounded-xl"
              >
                {CardContent}
              </button>
            );
          }

          return (
            <Link
              key={property.id}
              to={`/property/${property.propertyId}`}
              className="group focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)] rounded-xl"
            >
              {CardContent}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
